// 계약 변경 이력 (배치2, distributor-desk Design §2.5 · §4 · FR-12/13/14).
// 계약 하나를 골라 contract_amendments 타임라인(v1→v2→v3 서사)을 보고, 개정을
// apply_contract_amendment RPC(단일 트랜잭션)로 등록한다.
//
// ★ 달성률과의 분리(명시): 이 화면은 "계약 조건이 어떻게 바뀌었는가"의 서사 이력이다.
//   달성률/실적은 contract_sales 누적(확정 행)에서만 계산되며 개정은 과거 실적을
//   소급 변경하지 않는다.
//
// 컨텍스트(useDistributorData)는 읽기 전용 참조. 개정 등록 후에는 타임라인과 선택
// 계약을 자체 refresh 한다(Layout 컨텍스트는 갱신되지 않음).
import { useEffect, useMemo, useState } from "react";
import { isSupabaseConfigured } from "@/lib/supabase";
import { contractAmendmentsRepo, contractsRepo } from "@/lib/db";
import type {
  ContractAmendment,
  ContractAmendmentChangeType,
  ContractAmendmentInput,
  ContractAmendmentPatch,
  ContractRow,
  ContractStatus,
} from "@/types";
import {
  Badge,
  btnGhost,
  btnPrimary,
  btnSubtle,
  Card,
  cx,
  Disclaimer,
  Field,
  IconDoc,
  Input,
  PageHeader,
  Select,
  Textarea,
} from "./ui";
import { useDistributorData } from "./DistributorDeskLayout";

// ── labels / palette ─────────────────────────────────────────────────────────
const CHANGE_TYPES: ContractAmendmentChangeType[] = [
  "price",
  "moq",
  "exclusivity",
  "term",
  "quota",
  "license",
  "other",
];
const CHANGE_LABEL: Record<ContractAmendmentChangeType, string> = {
  price: "가격",
  moq: "MOQ",
  exclusivity: "독점권",
  term: "기간",
  quota: "쿼터",
  license: "인허가",
  other: "기타",
};
const STATUS_LABEL: Record<ContractStatus, string> = {
  active: "진행중",
  done: "종료",
  cancelled: "취소",
};

// ── helpers ──────────────────────────────────────────────────────────────────
const dayOnly = (s: string | null | undefined): string => (s ? s.slice(0, 10) : "—");

function fmtVal(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
/** before/after 델타를 "key: 이전 → 이후" 줄들로. */
function deltaText(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
): string {
  const b = before ?? {};
  const a = after ?? {};
  const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])];
  if (keys.length === 0) return "(델타 없음)";
  return keys.map((k) => `${k}: ${fmtVal(b[k])} → ${fmtVal(a[k])}`).join("\n");
}

interface FormState {
  changeTypes: Set<ContractAmendmentChangeType>;
  effective_date: string;
  reason: string;
  approved_by: string;
  target_qty: string;
  start_date: string;
  end_date: string;
  quota_start: string;
  status: ContractStatus;
  note: string;
  tiers: string; // JSON
  free: string; // JSON
}

/** 선택 계약의 현재 값으로 폼 초기화 (after 후보 + before 스냅샷 기준선). */
function prefill(c: ContractRow | null): FormState {
  return {
    changeTypes: new Set(),
    effective_date: "",
    reason: "",
    approved_by: "",
    target_qty: c?.target_qty != null ? String(c.target_qty) : "",
    start_date: c?.start_date ?? "",
    end_date: c?.end_date ?? "",
    quota_start: c?.quota_start ?? "",
    status: c?.status ?? "active",
    note: c?.note ?? "",
    tiers: JSON.stringify(c?.tiers ?? [], null, 2),
    free: JSON.stringify(c?.free ?? {}, null, 2),
  };
}

export function ContractChangesPage(): JSX.Element {
  const d = useDistributorData();
  const configured = isSupabaseConfigured();

  const [contractId, setContractId] = useState<string>("");
  const [sel, setSel] = useState<ContractRow | null>(null); // 폼 기준선 + before 소스
  const [amendments, setAmendments] = useState<ContractAmendment[]>([]);
  const [loadingAmend, setLoadingAmend] = useState(false);
  const [form, setForm] = useState<FormState>(() => prefill(null));
  const [jsonOpen, setJsonOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // 계약 선택 목록 (이름순).
  const contractOptions = useMemo(
    () => [...d.contracts].sort((a, b) => a.name.localeCompare(b.name, "ko")),
    [d.contracts],
  );

  // 선택 변경 → 기준선/타임라인/폼 로드.
  useEffect(() => {
    if (!contractId) {
      setSel(null);
      setAmendments([]);
      setForm(prefill(null));
      return;
    }
    const ctx = d.contracts.find((c) => c.id === contractId) ?? null;
    setSel(ctx);
    setForm(prefill(ctx));
    setJsonOpen(false);
    if (!configured) {
      setAmendments([]);
      return;
    }
    let alive = true;
    setLoadingAmend(true);
    (async () => {
      try {
        const rows = await contractAmendmentsRepo.listByContract(contractId);
        if (alive) setAmendments(rows);
      } catch {
        if (alive) setAmendments([]);
      }
      // 최신 계약값으로 기준선 갱신(선택 직후 — 사용자 편집 전).
      try {
        const fresh = await contractsRepo.get(contractId);
        if (alive && fresh) {
          setSel(fresh);
          setForm(prefill(fresh));
        }
      } catch {
        /* keep ctx baseline */
      }
      if (alive) setLoadingAmend(false);
    })();
    return () => {
      alive = false;
    };
  }, [contractId, d.contracts, configured]);

  const toggleType = (t: ContractAmendmentChangeType) =>
    setForm((f) => {
      const s = new Set(f.changeTypes);
      if (s.has(t)) s.delete(t);
      else s.add(t);
      return { ...f, changeTypes: s };
    });

  async function handleSubmit(): Promise<void> {
    if (!sel) return;
    if (!configured) {
      window.alert("Supabase 미설정 — 개정을 등록할 수 없습니다.");
      return;
    }
    if (form.changeTypes.size === 0) {
      window.alert("변경 유형(가격/MOQ/독점권/기간/쿼터/인허가/기타)을 하나 이상 선택하세요.");
      return;
    }

    // JSON 텍스트에어리어 파싱(고급).
    let tiersVal: unknown;
    let freeVal: unknown;
    try {
      tiersVal = JSON.parse(form.tiers.trim() === "" ? "[]" : form.tiers);
    } catch {
      window.alert("tiers JSON 형식이 올바르지 않습니다. 확인 후 다시 시도하세요.");
      return;
    }
    try {
      freeVal = JSON.parse(form.free.trim() === "" ? "{}" : form.free);
    } catch {
      window.alert("free JSON 형식이 올바르지 않습니다. 확인 후 다시 시도하세요.");
      return;
    }

    const base = sel; // before 스냅샷 = 폼을 만든 계약 스냅샷 (내부 일관성 보장).
    const patch: ContractAmendmentPatch = {};
    const before: Record<string, unknown> = {};
    const after: Record<string, unknown> = {};

    // target_qty
    const newQty = form.target_qty.trim() === "" ? null : Number(form.target_qty);
    if (newQty !== null && !Number.isFinite(newQty)) {
      window.alert("목표 수량은 숫자여야 합니다.");
      return;
    }
    if ((newQty ?? null) !== (base.target_qty ?? null)) {
      patch.target_qty = newQty;
      before.target_qty = base.target_qty;
      after.target_qty = newQty;
    }

    // date-like string fields
    const cmpStr = (
      formVal: string,
      cur: string | null,
      key: "start_date" | "end_date" | "quota_start",
    ): void => {
      const nv = formVal.trim() === "" ? null : formVal;
      if ((nv ?? null) !== (cur ?? null)) {
        patch[key] = nv;
        before[key] = cur;
        after[key] = nv;
      }
    };
    cmpStr(form.start_date, base.start_date, "start_date");
    cmpStr(form.end_date, base.end_date, "end_date");
    cmpStr(form.quota_start, base.quota_start, "quota_start");

    // status
    if (form.status !== base.status) {
      patch.status = form.status;
      before.status = base.status;
      after.status = form.status;
    }
    // note
    if (form.note !== (base.note ?? "")) {
      patch.note = form.note;
      before.note = base.note;
      after.note = form.note;
    }
    // tiers / free (deep compare via JSON)
    if (JSON.stringify(tiersVal) !== JSON.stringify(base.tiers ?? [])) {
      patch.tiers = tiersVal;
      before.tiers = base.tiers;
      after.tiers = tiersVal;
    }
    if (JSON.stringify(freeVal) !== JSON.stringify(base.free ?? {})) {
      patch.free = freeVal;
      before.free = base.free;
      after.free = freeVal;
    }

    if (Object.keys(patch).length === 0) {
      window.alert("변경된 계약 필드가 없습니다. 값을 수정한 뒤 등록하세요.");
      return;
    }

    const amendment: ContractAmendmentInput = {
      change_types: [...form.changeTypes],
      effective_date: form.effective_date.trim() === "" ? null : form.effective_date,
      before_json: before,
      after_json: after,
      reason: form.reason,
      approved_by: form.approved_by.trim() === "" ? null : form.approved_by,
      created_by: null,
    };

    setSaving(true);
    try {
      await contractAmendmentsRepo.apply(base.id, patch, amendment);
      // refresh: 타임라인 + 최신 계약값 → 기준선/폼 재설정.
      const [rows, fresh] = await Promise.all([
        contractAmendmentsRepo.listByContract(base.id),
        contractsRepo.get(base.id),
      ]);
      setAmendments(rows);
      const next = fresh ?? base;
      setSel(next);
      setForm(prefill(next));
      setJsonOpen(false);
    } catch (e) {
      window.alert("개정 등록에 실패했습니다: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Amendments"
        title="계약 변경 이력"
        subtitle="계약 조건 개정의 서사 이력 · 달성률(contract_sales)과 별개로 관리됩니다."
      />

      {/* 달성률 분리 안내 — 정제된 notice */}
      <Card className="px-4 py-3.5">
        <div className="flex items-start gap-3">
          <Badge tone="info" dot className="mt-0.5 shrink-0">
            달성률과 별개
          </Badge>
          <p className="m-0 text-[13px] leading-relaxed text-slate-600">
            이 이력은 <b className="font-semibold text-slate-800">계약 조건 개정의 서사</b>입니다.
            달성률·실적(
            <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px] text-slate-600">
              contract_sales
            </code>
            )과는 별개이며, 개정은{" "}
            <b className="font-semibold text-slate-800">과거 실적을 소급 변경하지 않습니다</b>. 각 실적
            행이 당시 가격·환율·원가를 자체 보유하므로 조건 개정이 지나간 실적을 왜곡하지 않습니다.
          </p>
        </div>
      </Card>

      {!configured && (
        <Card className="px-4 py-3.5">
          <div className="flex items-start gap-3">
            <Badge tone="warn" dot className="mt-0.5 shrink-0">
              미설정
            </Badge>
            <p className="m-0 text-[13px] leading-relaxed text-slate-600">
              Supabase 미설정 — 타임라인 조회·개정 등록이 비활성화됩니다.{" "}
              <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px] text-slate-600">
                .env
              </code>{" "}
              설정 후 사용하세요.
            </p>
          </div>
        </Card>
      )}

      {/* 계약 선택 */}
      <Card className="px-4 py-4">
        <Field label="계약 선택">
          {d.loading ? (
            <p className="m-0 text-[13px] text-slate-400">불러오는 중…</p>
          ) : contractOptions.length === 0 ? (
            <p className="m-0 text-[13px] leading-relaxed text-slate-500">
              등록된 계약이 없습니다. 계약을 먼저 등록한 뒤 개정 이력을 남길 수 있습니다.
            </p>
          ) : (
            <Select
              value={contractId}
              onChange={(e) => setContractId(e.target.value)}
              className="max-w-xl"
            >
              <option value="">— 계약을 선택하세요 —</option>
              {contractOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.destination_country}
                  {c.distributor_relationship_id ? " · 총판연결" : " · 미연결"}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {sel && (
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 border-t border-slate-100 pt-3 text-[12px] text-slate-500">
            <span>
              현재 상태 <span className="font-semibold text-slate-800">{STATUS_LABEL[sel.status]}</span>
            </span>
            <span>
              목표수량{" "}
              <span className="tnum font-semibold text-slate-800">
                {sel.target_qty != null ? sel.target_qty.toLocaleString("ko-KR") : "—"}
              </span>
            </span>
            <span>
              계약기간{" "}
              <span className="tnum font-semibold text-slate-800">
                {dayOnly(sel.start_date)} ~ {dayOnly(sel.end_date)}
              </span>
            </span>
            <span>
              쿼터시작 <span className="tnum font-semibold text-slate-800">{dayOnly(sel.quota_start)}</span>
            </span>
            {!sel.distributor_relationship_id && <Badge tone="warn">총판 관계 미연결</Badge>}
          </div>
        )}
      </Card>

      {sel && (
        <div className="grid gap-5 lg:grid-cols-2">
          {/* ── 개정 타임라인 ── */}
          <Card className="px-4 py-4" as="section">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <IconDoc className="h-[18px] w-[18px] text-indigo-400" />
              <h3 className="m-0 text-[13px] font-semibold text-slate-800">개정 타임라인</h3>
              <span className="text-[11px] text-slate-400">v1 → v2 → v3 (조건 개정 서사)</span>
            </div>
            {loadingAmend ? (
              <p className="m-0 text-[13px] text-slate-400">불러오는 중…</p>
            ) : amendments.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 px-4 py-8 text-center text-[13px] leading-relaxed text-slate-400">
                아직 등록된 개정이 없습니다. 우측 양식으로 첫 개정을 기록하세요.
              </div>
            ) : (
              <ol className="space-y-4">
                {amendments.map((a) => (
                  <li key={a.id} className="relative border-l-2 border-indigo-100 pl-4">
                    <span className="absolute -left-[7px] top-1 h-3 w-3 rounded-full border-2 border-indigo-300 bg-white" />
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="tnum rounded-full bg-indigo-600 px-2 py-0.5 text-[11px] font-semibold text-white">
                        v{a.amendment_no}
                      </span>
                      {a.change_types.map((t) => (
                        <Badge key={t} tone="accent">
                          {CHANGE_LABEL[t] ?? t}
                        </Badge>
                      ))}
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                      <span>개정일 {dayOnly(a.amended_at)}</span>
                      <span>발효일 {dayOnly(a.effective_date)}</span>
                      {a.approved_by && <span>승인 {a.approved_by}</span>}
                    </div>
                    {a.reason && <p className="mt-1.5 text-[13px] leading-relaxed text-slate-700">{a.reason}</p>}
                    <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-slate-50 px-3 py-2 font-mono text-[11px] leading-relaxed text-slate-600 ring-1 ring-inset ring-slate-100">
                      {deltaText(a.before_json, a.after_json)}
                    </pre>
                  </li>
                ))}
              </ol>
            )}
          </Card>

          {/* ── 개정 등록 ── */}
          <Card className="px-4 py-4" as="section">
            <h3 className="m-0 mb-4 text-[13px] font-semibold text-slate-800">개정 등록</h3>

            {/* 변경 유형 */}
            <div className="mb-4">
              <div className="mb-1.5 text-[11px] font-medium text-slate-500">변경 유형 (다중 선택)</div>
              <div className="flex flex-wrap gap-1.5">
                {CHANGE_TYPES.map((t) => {
                  const on = form.changeTypes.has(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleType(t)}
                      className={cx(
                        "rounded-lg border px-2.5 py-1 text-[12px] font-medium transition",
                        on
                          ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                          : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50",
                      )}
                    >
                      {CHANGE_LABEL[t]}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 발효일 / 승인자 */}
            <div className="mb-4 grid gap-3 sm:grid-cols-2">
              <Field label="발효일">
                <Input
                  type="date"
                  value={form.effective_date}
                  onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))}
                />
              </Field>
              <Field label="승인자">
                <Input
                  value={form.approved_by}
                  onChange={(e) => setForm((f) => ({ ...f, approved_by: e.target.value }))}
                  placeholder="예: 홍길동 이사"
                />
              </Field>
            </div>

            {/* 사유 */}
            <div className="mb-4">
              <Field label="사유">
                <Textarea
                  value={form.reason}
                  onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                  rows={2}
                  placeholder="개정 배경·근거를 기록하세요."
                />
              </Field>
            </div>

            {/* 델타 입력 */}
            <div className="mb-2 text-[11px] font-medium text-slate-500">
              변경할 계약 필드 (현재값 프리필 · 수정한 항목만 개정에 반영)
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="목표수량">
                <Input
                  value={form.target_qty}
                  onChange={(e) => setForm((f) => ({ ...f, target_qty: e.target.value }))}
                  placeholder="미설정"
                />
              </Field>
              <Field label="상태">
                <Select
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as ContractStatus }))}
                >
                  <option value="active">진행중</option>
                  <option value="done">종료</option>
                  <option value="cancelled">취소</option>
                </Select>
              </Field>
              <Field label="계약 시작일">
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                />
              </Field>
              <Field label="계약 종료일">
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                />
              </Field>
              <Field label="쿼터 시작일">
                <Input
                  type="date"
                  value={form.quota_start}
                  onChange={(e) => setForm((f) => ({ ...f, quota_start: e.target.value }))}
                />
              </Field>
              <Field label="메모">
                <Input
                  value={form.note}
                  onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="계약 메모"
                />
              </Field>
            </div>

            {/* 고급: tiers / free JSON */}
            <div className="mt-4">
              <button type="button" onClick={() => setJsonOpen((o) => !o)} className={btnSubtle}>
                {jsonOpen
                  ? "▲ 고급: 가격구간(tiers)·무상(free) JSON 닫기"
                  : "▼ 고급: 가격구간(tiers)·무상(free) JSON 편집"}
              </button>
              {jsonOpen && (
                <div className="mt-3 grid gap-3">
                  <Field label="tiers (JSON 배열)">
                    <Textarea
                      value={form.tiers}
                      onChange={(e) => setForm((f) => ({ ...f, tiers: e.target.value }))}
                      rows={5}
                      spellCheck={false}
                      className="font-mono"
                    />
                  </Field>
                  <Field label="free (JSON 객체)">
                    <Textarea
                      value={form.free}
                      onChange={(e) => setForm((f) => ({ ...f, free: e.target.value }))}
                      rows={4}
                      spellCheck={false}
                      className="font-mono"
                    />
                  </Field>
                </div>
              )}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving || !configured}
                className={btnPrimary}
              >
                {saving ? "등록 중…" : "개정 등록"}
              </button>
              <button
                type="button"
                onClick={() => setForm(prefill(sel))}
                disabled={saving}
                className={btnGhost}
              >
                현재값으로 초기화
              </button>
              <span className="text-[11px] text-slate-400">
                등록 시 계약값이 갱신되고 개정 이력이 append 됩니다(단일 트랜잭션).
              </span>
            </div>
          </Card>
        </div>
      )}

      <Disclaimer>
        본 도구는 사전 정의된 항목만 취합·표시합니다. 최종 판단·확인은 담당자 책임입니다.
      </Disclaimer>
    </div>
  );
}
